import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useSocialProfiles } from "@/lib/useSocialProfiles";
import { PlatformIcon } from "@/lib/platformIcons";

const initial = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  message: "",
};

const EVENT_TYPE_KEYS = [
  "clubNight",
  "festival",
  "privateEvent",
  "wedding",
  "corporate",
  "collaboration",
  "other",
];

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { music_platforms, social_media, loading: socialLoading } = useSocialProfiles();
  const isAr = (i18n.language || "").startsWith("ar");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(initial);
      toast.success(t("contact.form.sentToast"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("contact.form.failToast"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative py-32 md:py-48 px-6 md:px-10 bg-black"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-xs text-red-brand tracking-[0.3em] uppercase">
            {t("contact.kicker")}
          </p>
          <h2 className="font-anton mt-4 text-6xl md:text-8xl leading-none text-white">
            {t("contact.title")}
          </h2>
          <p className="mt-6 text-white/60 max-w-md leading-relaxed">
            {t("contact.lead")}
          </p>

          <div className="mt-10 space-y-4 font-mono text-sm">
            <div>
              <div className="text-white/40 text-[10px] tracking-[0.3em]">{t("contact.emailLabel")}</div>
              <div className="text-white mt-1" dir="ltr">bookings@djspyofficial.com</div>
            </div>
            <div>
              <div className="text-white/40 text-[10px] tracking-[0.3em]" data-testid="contact-music-label">
                {isAr ? "منصات الموسيقى" : "MUSIC PLATFORMS"}
              </div>
              <div className="flex flex-wrap gap-2 mt-2" dir="ltr" data-testid="contact-music-list">
                {(!socialLoading) && music_platforms.map((s) => (
                  <a
                    key={s.platform_slug}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`DJ SPY on ${s.display_name}`}
                    data-testid={`contact-music-${s.platform_slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-red-brand/40 px-3 py-1.5 text-white/80 hover:text-red-brand focus:outline-none focus:ring-2 focus:ring-red-brand/50 transition-colors"
                  >
                    <PlatformIcon slug={s.icon_hint || s.platform_slug} size={14} />
                    <span className="text-xs">{s.display_name}</span>
                  </a>
                ))}
              </div>
            </div>
            <div>
              <div className="text-white/40 text-[10px] tracking-[0.3em]" data-testid="contact-social-label">
                {isAr ? "وسائل التواصل الاجتماعي" : t("contact.socialLabel")}
              </div>
              <div className="flex flex-wrap gap-2 mt-2" dir="ltr" data-testid="contact-social-list">
                {(!socialLoading) && social_media.map((s) => (
                  <a
                    key={s.platform_slug}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`DJ SPY on ${s.display_name}`}
                    data-testid={`social-${s.platform_slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-red-brand/40 px-3 py-1.5 text-white/80 hover:text-red-brand focus:outline-none focus:ring-2 focus:ring-red-brand/50 transition-colors"
                  >
                    <PlatformIcon slug={s.icon_hint || s.platform_slug} size={14} />
                    <span className="text-xs">{s.display_name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="col-span-12 md:col-span-7 hairline rounded-2xl p-6 md:p-10 bg-white/[0.02]"
          data-testid="contact-form"
        >
          {sent ? (
            <div className="flex flex-col items-center text-center py-10">
              <CheckCircle2 size={48} className="text-red-brand" />
              <div className="font-display text-2xl text-white mt-4">
                {t("contact.form.sentTitle")}
              </div>
              <p className="text-white/60 mt-2 max-w-md">
                {t("contact.form.sentBody")}
              </p>
              <button onClick={() => setSent(false)} data-testid="contact-send-another" className="btn-ghost mt-6">
                {t("contact.form.sendAnother")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.name")}</label>
                <input data-testid="contact-name" name="name" required value={form.name} onChange={onChange} placeholder={t("contact.form.namePlaceholder")} />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.email")}</label>
                <input data-testid="contact-email" name="email" type="email" required value={form.email} onChange={onChange} placeholder={t("contact.form.emailPlaceholder")} dir="ltr" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.phone")}</label>
                <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} placeholder={t("contact.form.phonePlaceholder")} dir="ltr" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.eventType")}</label>
                <select data-testid="contact-event-type" name="event_type" value={form.event_type} onChange={onChange}>
                  <option value="">{t("contact.form.eventTypeSelect")}</option>
                  {EVENT_TYPE_KEYS.map((k) => (
                    <option key={k} value={t(`contact.eventTypes.${k}`, { lng: "en" })}>
                      {t(`contact.eventTypes.${k}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.eventDate")}</label>
                <input data-testid="contact-event-date" name="event_date" type="date" value={form.event_date} onChange={onChange} dir="ltr" />
              </div>
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">{t("contact.form.message")}</label>
                <textarea data-testid="contact-message" name="message" required rows={5} value={form.message} onChange={onChange} placeholder={t("contact.form.messagePlaceholder")} />
              </div>
              <div className="col-span-2">
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                  {loading ? t("contact.form.sending") : t("contact.form.submit")}
                </button>
              </div>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}
